import React, { useEffect, useState, useRef } from "react";
import { loadBondConfig, localBonds } from "./config";
import { BondService } from "./service";
import {
  TIERS,
  price,
  area,
  parseAmount,
  human,
  number,
  rawText,
} from "./model";
import { getInjectedProvider } from "../utils/walletProvider";
import BondCurve from "./BondCurve";
import { useLiveVesting } from "./useLiveVesting";
import { useBondReceipts } from "./useBondReceipts";
import "./bonds.css";
import { errorText } from "./errors";
const date = (seconds) =>
  new Date(Number(seconds) * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
export default function BondsPanel({ onConnectWallet } = {}) {
  const [service, setService] = useState(null),
    [boot, setBoot] = useState(true),
    [state, setState] = useState(null),
    [account, setAccount] = useState(""),
    [chain, setChain] = useState(0);
  const [quantity, setQuantity] = useState("1000000"),
    [tier, setTier] = useState(1),
    [payment, setPayment] = useState("USDC"),
    [quoteResult, setQuote] = useState(null),
    [quoting, setQuoting] = useState(false),
    [quoteError, setQuoteError] = useState("");
  const [balances, setBalances] = useState(null);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const receiptSnapshot = useBondReceipts(service, account, refresh, confirmation);
  const { error: holdingsError, loading: holdingsLoading, updatedAt: holdingsUpdatedAt } = receiptSnapshot;
  const { rows, stale: holdingsStale } = useLiveVesting(receiptSnapshot);
  const [dataError, setDataError] = useState("");
  const [error, setError] = useState(""),
    [status, setStatus] = useState(""),
    [hash, setHash] = useState(""),
    [busy, setBusy] = useState(false),
    [transfer, setTransfer] = useState(null),
    [recipient, setRecipient] = useState("");
  const lock = useRef(false);
  const lastQuote = useRef(null);
  const [quoteTick, setQuoteTick] = useState(0);
  const [amountHint, setAmountHint] = useState("");
  const [txStage, setTxStage] = useState("");
  const [txScope, setTxScope] = useState("");
  const [configRetry, setConfigRetry] = useState(0);
  const soldRaw = state?.sold;
  const inventoryRaw = state?.inventory;
  const unavailable = state && (state.closed || state.paused || state.inventory === 0n);
  const quoteKey = `${quantity}:${tier}:${payment}:${soldRaw}`;
  const quote = quoteResult?.key === quoteKey ? quoteResult : null;
  const setAmount = (value) => {
    setAmountHint("");
    const cleaned = value.trim().replace(/,/g, "");
    try {
      if (inventoryRaw !== undefined && parseAmount(cleaned) > inventoryRaw) {
        setQuantity(rawText(inventoryRaw));
        setAmountHint("Set to the available inventory.");
        return;
      }
    } catch {
      /* Keep incomplete input editable; validation below handles it. */
    }
    setQuantity(cleaned);
  };
  useEffect(() => {
    if (inventoryRaw === undefined) return;
    try {
      if (parseAmount(quantity) > inventoryRaw) {
        setQuantity(rawText(inventoryRaw));
        setAmountHint("Set to the available inventory.");
      }
    } catch {
      /* Incomplete input. */
    }
  }, [inventoryRaw, quantity]);
  useEffect(() => {
    const refreshQuote = () => setQuoteTick((n) => n + 1);
    const timer = setInterval(refreshQuote, 30000);
    window.addEventListener("focus", refreshQuote);
    return () => { clearInterval(timer); window.removeEventListener("focus", refreshQuote); };
  }, []);
  useEffect(() => {
    let active = true;
    setBoot(true);
    loadBondConfig()
      .then((config) => {
        if (active && config) setService(new BondService(config));
      })
      .catch((e) => active && setError(errorText(e)))
      .finally(() => active && setBoot(false));
    return () => {
      active = false;
    };
  }, [configRetry]);
  useEffect(() => {
    let active = true;
    const read = async () => {
      const p = getInjectedProvider();
      try {
        const accounts = p ? await p.request({ method: "eth_accounts" }) : [];
        const id = p ? Number(await p.request({ method: "eth_chainId" })) : 0;
        if (active) {
          setAccount(
            localStorage.getItem("walletDisconnected") === "true"
              ? ""
              : accounts[0] || "",
          );
          setChain(id);
        }
      } catch {
        if (active) {
          setAccount("");
          setChain(0);
        }
      }
    };
    read();
    const t = setInterval(read, 1500);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    if (!service) return;
    let active = true;
    let loading = false;
    const read = async () => {
      if (loading) return;
      loading = true;
      try {
        const s = await service.state();
        if (active) {
          setState(s);
          setDataError("");
        }
      } catch (e) {
        if (active) {
          setDataError(errorText(e));
        }
      } finally {
        loading = false;
      }
    };
    read();
    const t = setInterval(read, 15000);
    window.addEventListener("focus", read);
    return () => {
      active = false;
      window.removeEventListener("focus", read);
      clearInterval(t);
    };
  }, [service, refresh]);
  useEffect(() => {
    setBalances(null);
  }, [service, account]);
  useEffect(() => {
    if (!service || !account || !state) return;
    let active = true;
    Promise.all([
      service.usdc.methods.balanceOf(account).call({}, state.block),
      service.web3.eth.getBalance(account, state.block),
      service.usdc.methods
        .allowance(account, service.config.sale)
        .call({}, state.block),
    ])
      .then(([usdc, eth, allowance]) => {
        if (active) setBalances({ usdc, eth, allowance });
      })
      .catch(() => {
        if (active) setBalances(null);
      });
    return () => {
      active = false;
    };
  }, [service, account, state]);
  useEffect(() => {
    let active = true;
    setQuoteError("");
    if (!quantity.trim() || !service || soldRaw === undefined || inventoryRaw === undefined || unavailable || dataError) {
      setQuote(null);
      lastQuote.current = null;
      setQuoting(false);
      return;
    }
    let amount;
    try {
      amount = parseAmount(quantity);
      if (amount > inventoryRaw)
        throw new Error("This exceeds the available sale inventory.");
    } catch (e) {
      setQuote(null);
      lastQuote.current = null;
      setQuoting(false);
      setQuoteError(errorText(e));
      return;
    }
    // Retain a valid quote during silent ETH refreshes. Ordinary block polling
    // does not invalidate a quote when sold/inventory have not changed.
    setQuoting(lastQuote.current?.key !== quoteKey);
    const timer = setTimeout(async () => {
      try {
        const q = await service.quote(amount, tier);
        if (q.sold !== soldRaw) {
          if (active) setRefresh((n) => n + 1);
          throw new Error("The sale has advanced. Updating your allocation and price…");
        }
        if (q.cost === 0n)
          throw new Error("This amount is too small to purchase.");
        if (q.cost < 1000000n && amount !== inventoryRaw)
          throw new Error("The minimum purchase is 1 USDC.");
        if (payment === "ETH") {
          const nq = await service.nativeQuote(amount, tier, q.block);
          if (nq.cost !== q.cost) throw new Error("Payment quotes differ. Please refresh before buying.");
          q.eth = nq.ethRequired;
          q.ethMax = (nq.ethRequired * 10100n + 9999n) / 10000n;
        }
        q.key = quoteKey;
        if (active) {
          lastQuote.current = q;
          setQuote(q);
        }
      } catch (e) {
        if (active) {
          setQuote(null);
          lastQuote.current = null;
          setQuoteError(errorText(e));
        }
      } finally {
        if (active) setQuoting(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    service,
    soldRaw,
    inventoryRaw,
    quantity,
    tier,
    payment,
    quoteKey,
    refresh,
    quoteTick,
    unavailable,
    dataError,
  ]);
  const sold = state ? human(state.sold) : 0,
    amount =
      Number(quantity) > 0 && Number.isFinite(Number(quantity))
        ? Math.min(
            Number(quantity),
            state ? human(state.inventory) : 1000000000000,
          )
        : 0;
  const estimate = area(sold + amount, tier) - area(sold, tier),
    cost = quote ? human(quote.cost, 6) : estimate;
  const allocationTotal = state
    ? human(state.sold + state.inventory)
    : 200000000;
  const ethValue = quote?.eth !== undefined ? human(quote.eth) : null;
  const rate = ethValue > 0 ? cost / ethValue : null;
  const needsApproval =
    payment === "USDC" && quote && balances && balances.allowance < quote.cost;
  const insufficient =
    quote &&
    balances &&
    (payment === "ETH"
      ? balances.eth < quote.ethMax
      : balances.usdc < quote.cost);
  const wrong = service && account && chain !== Number(service.config.chainId);
  const blocked = unavailable;
  const reason = state?.closed
    ? "Sale closed"
    : state?.paused
      ? "Purchases paused"
      : state?.inventory === 0n
        ? "No inventory available"
        : "";
  const update = (message, txHash, stage) => {
    if (stage) setTxStage(stage);
    setStatus(message);
    if (txHash) setHash(txHash);
  };
  const act = async (fn, success, scope = "purchase") => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setTxScope(scope);
    if (scope === "purchase") setPurchaseSummary(null);
    setError("");
    setStatus("Preparing transaction…");
    setHash("");
    setTxStage("");
    try {
      const tx = await fn();
      if (tx.blockNumber) service.lastConfirmedBlock = tx.blockNumber;
      setConfirmation({ account, hash: tx.transactionHash });
      setHash(tx.transactionHash);
      setStatus(success);
      if (scope === "purchase") {
        setPurchaseSummary({ amount: quote.amount, cost: quote.cost, tier: quote.tier });
        setQuantity("");
        setQuote(null);
        lastQuote.current = null;
        setAmountHint("Purchase confirmed. Choose a new amount to buy again.");
      }
      setTxStage("done");
      setTransfer(null);
      setRecipient("");
      setRefresh((n) => n + 1);
    } catch (e) {
      setError(errorText(e));
      if (e.transactionHash) setHash(e.transactionHash);
      setTxStage("error");
      setStatus(
        e.transactionPending
          ? "Confirmation unknown. Check the submitted transaction before retrying."
          : scope === "purchase" && payment === "USDC"
          ? "Request not completed. Any confirmed USDC approval is kept for retry."
          : "Request not completed. You can try again.",
      );
      setRefresh((n) => n + 1);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const switchNetwork = async () => {
    setTxScope("");
    try {
      const p = getInjectedProvider();
      if (!p) throw new Error("Connect your wallet first.");
      try {
        await p.request({
          method: "wallet_switchEthereumChain",
          params: [
            { chainId: `0x${Number(service.config.chainId).toString(16)}` },
          ],
        });
      } catch (e) {
        if (e.code !== 4902) throw e;
        await p.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: `0x${Number(service.config.chainId).toString(16)}`,
              chainName: service.config.chainName,
              rpcUrls: [service.config.rpc],
              nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
            },
          ],
        });
      }
    } catch (e) {
      setError(errorText(e));
    }
  };
  const totals = rows.reduce(
    (s, r) => ({
      remaining: s.remaining + human(r.remaining),
      claimed: s.claimed + human(r.claimed),
      claimable: s.claimable + human(r.claimableNow),
    }),
    { remaining: 0, claimed: 0, claimable: 0 },
  );
  const explorer = service?.config.explorer;
  const transactionFeedback = (
    <>
      {(status || error) && (
        <div
          className={`bond-status ${txStage === "error" ? "failed" : ""}`}
          role="status"
        >
          {error && <p role="alert">{error}</p>}
          {status}
          {txScope === "purchase" && txStage === "done" && purchaseSummary && (
            <p className="bond-confirmed-summary">
              <strong>{number(human(purchaseSummary.amount))} ADBYTES</strong>
              {" · "}{TIERS[purchaseSummary.tier].label}
              {" · "}{number(Number(purchaseSummary.cost) / 1e6, 6)} USDC purchase value
              <br />{purchaseSummary.tier === 0 ? "Tokens delivered; your Bond NFT is a fully claimed receipt." : "Your tokens unlock continuously. Claim any available amount from your Bond NFT below."}
            </p>
          )}
          {hash &&
            (explorer ? (
              <a
                href={`${explorer}/tx/${hash}`}
                target="_blank"
                rel="noreferrer"
              >
                View transaction
              </a>
            ) : (
              <code title={hash}>{hash.slice(0, 18)}…</code>
            ))}
          {txScope === "purchase" && txStage === "done" && (
            <button className="bond-secondary" onClick={() => {
              const holdings = document.getElementById("bond-holdings");
              holdings?.scrollIntoView({ behavior: "smooth", block: "start" });
              holdings?.focus({ preventScroll: true });
            }}>View my Bond NFTs</button>
          )}
        </div>
      )}
    </>
  );
  return (
    <section className="bonds-panel" aria-label="ADBYTES bonds">
      <header className="bond-heading">
        <div>
          <h1>Choose your time. Shape your allocation.</h1>
          <p>
            Buy ADBYTES on a transparent curve. Take delivery now, or choose a
            lower price with gradual release.
          </p>
        </div>
        <span className={`bond-network ${localBonds ? "local" : ""}`}>
          {localBonds ? "Local testnet · test funds only" : "Base"}
        </span>
      </header>
      {!boot && !service && (
        <div className="bond-notice">
          Bonds are not live on this network yet. Explore the launch curve
          below; purchases open when the contracts are configured.
        </div>
      )}
      {boot && <p role="status">Loading bond configuration…</p>}
      {blocked && (
        <div className="bond-notice" role="status">
          <strong>{reason}.</strong>{" "}
          {state.closed ? "This sale is closed to new purchases." : state.inventory === 0n
            ? "There are no ADBYTES available to buy right now."
            : "New purchases are temporarily unavailable."}{" "}
          Existing Bond NFT holders can still claim unlocked tokens below.
        </div>
      )}
      {((error && !txScope) || dataError) && (
        <div className="bond-error" role="alert">
          {dataError ? `Could not refresh sale data. ${dataError}` : error}
          <button
            onClick={() => {
              setError("");
              if (!service) setConfigRetry((n) => n + 1);
              else setRefresh((n) => n + 1);
            }}
          >
            Refresh
          </button>
        </div>
      )}
      <div className="bond-layout">
        <div className="bond-main">
          <section className="bond-surface">
            <div className="bond-section-title">
              <h2>The allocation curve</h2>
              <span>
                {state
                  ? `${dataError ? "Refresh delayed ·" : "Updated at"} block ${state.block}`
                  : service
                    ? "Loading sale data…"
                    : "Launch illustration"}
              </span>
            </div>
            <div className="bond-metrics">
              <div>
                <span>Cumulative sold</span>
                <strong>
                  {state ? number(sold) : "—"} <small>ADBYTES</small>
                </strong>
              </div>
              <div>
                <span>Available to buy</span>
                <strong>
                  {state ? number(human(state.inventory)) : "—"}{" "}
                  <small>ADBYTES</small>
                </strong>
              </div>
              <div>
                <span>Gross collected</span>
                <strong>
                  {state ? `${number(human(state.gross, 6))} USDC` : "—"}
                </strong>
              </div>
            </div>
            <div className="bond-progress-label">
              <span>
                {number(sold)} / {number(allocationTotal)} ADBYTES sold
              </span>
              <span>
                {allocationTotal > 0
                  ? number((sold / allocationTotal) * 100, 2)
                  : "0"}
                %
              </span>
            </div>
            <progress
              className="bond-progress"
              max={Math.max(allocationTotal, 1)}
              value={sold}
              aria-label="Available allocation sold"
            />
            <BondCurve
              sold={sold}
              amount={amount}
              tier={tier}
              total={allocationTotal}
              available={state ? human(state.inventory) : 0}
              disabled={busy || !state || !!dataError}
              onAllocationChange={(position) => {
                if (state)
                  setAmount(
                    rawText(
                      (state.inventory * window.BigInt(position)) / 10000n,
                    ),
                  );
              }}
            />
            <div className="bond-range">
              <div>
                <span>Your segment starts at</span>
                <strong>{number(sold)} sold</strong>
                <small>${price(sold, tier).toFixed(7)} / token</small>
              </div>
              <div>
                <span>Your segment ends at</span>
                <strong>{number(sold + amount)} sold</strong>
                <small>${price(sold + amount, tier).toFixed(7)} / token</small>
              </div>
            </div>
            <p className="bond-caption">
              Price follows tokens sold, not the DEX. Adding inventory extends
              the sale without resetting the curve.
            </p>
          </section>
          <section className="bond-explainer">
            <h2>Where your payment goes</h2>
            <div className="bond-flow">
              <div>
                <strong>70%</strong>
                <h3>Project treasury</h3>
                <p>Funds development, operations and marketing.</p>
              </div>
              <div>
                <strong>30%</strong>
                <h3>Permanent liquidity</h3>
                <p>
                  Added to the existing Uniswap v3 pool in your purchase transaction
                  as full-range liquidity. Its position NFT is permanently locked.
                </p>
              </div>
            </div>
            <p className="bond-caption">
              Existing LP is separate. Locked liquidity does not guarantee a
              token price.
            </p>
          </section>
        </div>
        <aside className="bond-surface bond-purchase">
          <h2>Build your allocation</h2>
          <label htmlFor="bond-quantity">How many ADBYTES?</label>
          <div className="bond-input-wrap">
            <input
              id="bond-quantity"
              inputMode="decimal"
              value={quantity}
              onChange={(e) => setAmount(e.target.value)}
              aria-describedby="bond-amount-help"
              aria-invalid={!!quoteError}
              autoComplete="off"
              disabled={busy}
            />
            <span>ADBYTES</span>
          </div>
          <p
            className="bond-caption bond-amount-help"
            id="bond-amount-help"
            aria-live="polite"
          >
            {state?.inventory === 0n ? "No tokens available. Inventory updates automatically." : amountHint ||
              (state
                ? `${number(human(state.inventory), 6)} ADBYTES available`
                : "Enter your allocation")}
          </p>
          <div className="bond-presets" aria-label="Quick allocation amounts">
            {[10000, 50000, 100000, 500000, 1000000, 5000000, 10000000].map(
              (n) => (
                <button
                  key={n}
                  disabled={busy || (state && n > human(state.inventory))}
                  aria-pressed={Number(quantity) === n}
                  onClick={() => setAmount(String(n))}
                >
                  {n < 1000000
                    ? `${number(n / 1000)}k`
                    : `${number(n / 1000000)}m`}
                </button>
              ),
            )}
            <button
              disabled={busy || !state || state.inventory === 0n}
              onClick={() => setAmount(rawText(state.inventory))}
            >
              Max available
            </button>
          </div>
          <fieldset disabled={busy}>
            <legend>Choose your release schedule</legend>
            <div className="bond-tiers">
              {TIERS.map((t, i) => (
                <label key={t.days} className={tier === i ? "selected" : ""}>
                  <input
                    type="radio"
                    name="vesting"
                    checked={tier === i}
                    onChange={() => setTier(i)}
                  />
                  <span>
                    {t.label}
                    <small>
                      {i === 0
                        ? "Tokens delivered now"
                        : "Linear release from purchase"}
                    </small>
                  </span>
                  <b>{t.discount === 0 ? "Full price" : `−${t.discount}%`}</b>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="bond-caption">
            Discounts are against the immediate sale price.{" "}
            {tier > 0
              ? "One Bond NFT per purchase. Tokens unlock continuously from day one."
              : "Tokens arrive immediately, with a fully claimed Bond NFT."}
          </p>
          {balances && (
            <p className="bond-caption">
              Wallet: {number(human(balances.usdc, 6), 4)} USDC ·{" "}
              {number(human(balances.eth), 6)} ETH
            </p>
          )}
          <fieldset className="bond-payment" disabled={busy}>
            <legend>Pay with</legend>
            {["USDC", "ETH"].map((p) => (
              <label key={p} className={payment === p ? "selected" : ""}>
                <input
                  type="radio"
                  name="payment"
                  disabled={p === "ETH" && !!service && !service.native}
                  checked={payment === p}
                  onChange={() => setPayment(p)}
                />
                {p}
              </label>
            ))}
          </fieldset>
          {service && !service.native && (
            <p className="bond-caption">
              ETH payments are not available for this sale.
            </p>
          )}
          {payment === "ETH" && (
            <p className="bond-caption">
              ETH converts to USDC in the same transaction. Unused ETH is
              refunded automatically.
            </p>
          )}
          <div className="bond-total" aria-busy={quoting}>
            <span>{service ? "Your quote" : "Estimated cost"}</span>
            <strong data-testid="bond-total">
              {payment === "ETH"
                ? ethValue === null
                  ? "—"
                  : number(ethValue, 10)
                : quote || !service
                  ? number(cost, 6)
                  : "—"}{" "}
              <small>{payment}</small>
            </strong>
            {payment === "ETH" && quote && (
              <p className="bond-equivalent">
                {number(cost, 6)} USDC equivalent
              </p>
            )}
            <dl>
              <div>
                <dt>Average per ADBYTES</dt>
                <dd data-testid="bond-unit-price">
                  {payment === "ETH"
                    ? ethValue === null || !amount
                      ? "—"
                      : `${(ethValue / amount).toFixed(12)} ETH`
                    : quote || !service
                      ? `${(amount ? cost / amount : 0).toFixed(7)} USDC`
                      : "—"}
                </dd>
              </div>
              {payment === "ETH" && rate && (
                <div>
                  <dt>Effective exchange rate</dt>
                  <dd>1 ETH ≈ {number(rate, 4)} USDC</dd>
                </div>
              )}
              <div>
                <dt>Full delivery</dt>
                <dd>
                  {TIERS[tier].days
                    ? `${TIERS[tier].days} days from purchase`
                    : "At purchase"}
                </dd>
              </div>
              {payment === "ETH" && quote && (
                <div>
                  <dt>
                    Maximum ETH sent <small>(1% buffer)</small>
                  </dt>
                  <dd>{number(human(quote.ethMax), 10)} ETH</dd>
                </div>
              )}
            </dl>
            {quote && <p className="bond-caption">Contract quote · block {String(quote.block)} · refreshes automatically</p>}
            <p className="bond-caption">
              {payment === "ETH"
                ? "Exchange rate includes this swap’s fees and price impact. Gas is extra."
                : "Network gas is paid separately in ETH."}
            </p>
          </div>
          <div className="bond-quote-status" aria-live="polite">
            {quoting && !reason && "Calculating your quote…"}
          </div>
          {quoteError && !blocked && (
            <div className="bond-inline-error" role="alert">
              <p>{quoteError}</p>
              <button type="button" disabled={busy || quoting} onClick={() => setQuoteTick(n => n + 1)}>Retry quote</button>
            </div>
          )}
          {reason && (
            <p className="bond-notice">
              {reason}.{" "}
              Existing bonds remain claimable.
            </p>
          )}
          {payment === "USDC" && account && quote && (
            <div className="bond-approval" aria-label="Purchase steps">
              <span
                className={
                  txScope === "purchase" && txStage === "approval"
                    ? "active"
                    : (busy &&
                          txScope === "purchase" &&
                          txStage === "purchase") ||
                        (balances && !needsApproval)
                      ? "complete"
                      : ""
                }
              >
                1.{" "}
                {busy && txScope === "purchase" && txStage === "purchase"
                  ? "USDC approved"
                  : !balances
                    ? "Checking approval…"
                    : needsApproval
                      ? "Approve USDC"
                      : "USDC approved"}
              </span>
              <span
                className={
                  txScope === "purchase" && txStage === "purchase"
                    ? "active"
                    : txScope === "purchase" && txStage === "done"
                      ? "complete"
                      : ""
                }
              >
                2. Buy {tier ? "bond" : "ADBYTES"}
              </span>
            </div>
          )}
          {insufficient && (
            <p className="bond-inline-error">
              Not enough {payment} in this wallet. Choose a smaller amount or
              add funds.
            </p>
          )}
          {balances?.eth === 0n && (
            <p className="bond-inline-error">
              Add ETH to this wallet for network gas.
            </p>
          )}
          {!account && !blocked && !dataError && state ? (
            <button className="bond-primary" disabled={busy} onClick={onConnectWallet}>Connect Wallet</button>
          ) : wrong ? (
            <button
              className="bond-primary"
              onClick={switchNetwork}
              disabled={busy}
            >
              Switch to {service.config.chainName}
            </button>
          ) : (
            <button
              className="bond-primary"
              disabled={
                !account ||
                !quote ||
                !state ||
                !!dataError ||
                !balances ||
                !!blocked ||
                busy ||
                quoting ||
                !!insufficient ||
                balances?.eth === 0n
              }
              onClick={() =>
                act(
                  () =>
                    service.purchase(
                      quote,
                      payment,
                      account,
                      update,
                      quote.ethMax,
                    ),
                  tier
                    ? "Purchase confirmed. Your Bond NFT and liquidity contribution are recorded on-chain."
                    : "Purchase confirmed. ADBYTES was delivered to your wallet.",
                )
              }
            >
              {busy
                ? txStage === "approval"
                  ? "Step 1 of 2 · Approving USDC…"
                  : txScope === "purchase" && txStage === "purchase"
                    ? "Confirming purchase…"
                    : "Transaction in progress…"
                : !service
                  ? boot
                    ? "Loading sale…"
                    : "Not live yet"
                  : reason
                    ? reason
                    : dataError
                      ? "Sale data unavailable"
                      : !state
                        ? "Loading sale…"
                        : !account
                    ? "Connect Wallet"
                    : !quantity.trim()
                      ? "Enter an ADBYTES amount"
                      : `Buy ${tier ? "bond" : "ADBYTES"} with ${payment}`}
            </button>
          )}
          <p className="bond-caption">
            {blocked ? "You do not need to approve or send funds while purchases are unavailable." : payment === "USDC"
              ? needsApproval
                ? "Two wallet confirmations: approve this amount, then buy. No unlimited approval."
                : !account || !balances
                  ? "We’ll check your USDC approval when your wallet is connected."
                  : "Existing approval covers this amount. One purchase confirmation."
              : "One wallet confirmation. The swap and purchase complete together."}{" "}
            Purchases are final.
          </p>
          {txScope === "purchase" && transactionFeedback}
        </aside>
      </div>
      <section id="bond-holdings" tabIndex={-1} className="bond-surface bond-holdings">
        <div className="bond-section-title">
          <div>
            <h2>Your Bond NFTs</h2>
            <p>
              Each Bond NFT is a separate allocation. Claims always pay its current
              owner.
            </p>
          </div>
          <button
            onClick={() => setRefresh((n) => n + 1)}
            disabled={busy || holdingsLoading}
          >
            {holdingsLoading ? "Refreshing…" : "Refresh balances"}
          </button>
        </div>
        {txScope === "receipts" && transactionFeedback}
        {account && holdingsUpdatedAt && (
          <p className="bond-caption" data-testid="receipt-freshness">
            Live vesting estimate · synced {new Date(holdingsUpdatedAt).toLocaleTimeString()}.
            {" "}The contract determines the exact amount when you claim.
            {holdingsError && !holdingsStale && " Refreshing in the background…"}
          </p>
        )}
        {account && holdingsStale && rows.length > 0 && <div className="bond-error" role="status">
          Live updates are temporarily delayed. Your last balance is shown; reconnecting automatically.
        </div>}
        {!account ? (
          <div className="bond-empty">
            Connect your wallet to see your Bond NFTs and claim unlocked
            ADBYTES.
            <button className="bond-primary" onClick={onConnectWallet}>Connect Wallet</button>
          </div>
        ) : !service ? (
          <div className="bond-empty">
            Bond NFTs will appear here when bonds launch.
          </div>
        ) : holdingsError && rows.length === 0 ? (
          <div className="bond-error" role="alert">
            Could not load Bond NFTs: {holdingsError}
          </div>
        ) : holdingsLoading && rows.length === 0 ? (
          <p role="status">Loading your Bond NFTs…</p>
        ) : rows.length === 0 ? (
          <div className="bond-empty">
            No Bond NFTs in this wallet yet. Every purchase creates one;
            transferred-in Bond NFTs appear here too.
          </div>
        ) : (
          <>
            <div className="bond-metrics">
              <div>
                <span>Remaining in owned Bond NFTs</span>
                <strong>{number(totals.remaining)}</strong>
              </div>
              <div>
                <span>Previously claimed from these Bond NFTs</span>
                <strong>{number(totals.claimed)}</strong>
              </div>
              <div>
                <span>Claimable now</span>
                <strong>{number(totals.claimable)}</strong>
              </div>
            </div>
            <div className="bond-receipts">
              {rows.map((r) => (
                <article key={r.id} className="bond-receipt" data-receipt-id={r.id}>
                  <div className="bond-section-title">
                    <h3>Bond NFT #{r.id}</h3>
                    <span>
                      {r.remaining === 0n
                        ? "Fully claimed"
                        : r.matured
                          ? "Fully vested"
                          : `Vesting until ${date(r.end)}`}
                    </span>
                  </div>
                  <strong className="bond-allocation">
                    {number(human(r.allocation))}{" "}
                    <small>ADBYTES allocation</small>
                  </strong>
                  <progress
                    max={100}
                    value={(human(r.vested) / human(r.allocation)) * 100}
                    aria-label={`Bond ${r.id} vested percentage`}
                  />
                  <p className="bond-caption">{number(human(r.vested) / human(r.allocation) * 100, 2)}% unlocked
                    {explorer && <> · <a href={`${explorer}/nft/${service.config.receipt}/${r.id}`} target="_blank" rel="noreferrer">View NFT ↗</a></>}
                  </p>
                  <dl>
                    {[
                      ["Claimable now", r.claimableNow],
                      ["Total claimed", r.claimed],
                      ["Remaining entitlement", r.remaining],
                      ["Still vesting", r.unvested],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd data-field={label} data-raw={value.toString()}>{number(human(value), 6)}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="bond-caption">
                    {date(r.start)} — {date(r.end)}. Transferring preserves the
                    schedule and all previous claims.
                  </p>
                  <div className="bond-actions">
                    <button
                      className="bond-primary"
                      disabled={busy || wrong || holdingsStale || r.claimableNow === 0n}
                      onClick={() =>
                        act(
                          () => service.claim(r.id, account, update),
                          "Claim confirmed. ADBYTES was sent to the current NFT owner.",
                          "receipts",
                        )
                      }
                    >
                      Claim ADBYTES
                    </button>
                    <button
                      disabled={busy || wrong || holdingsStale}
                      onClick={() => {
                        setTransfer(r.id);
                        setRecipient("");
                      }}
                    >
                      Transfer Bond NFT
                    </button>
                  </div>
                  {transfer === r.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        act(
                          () =>
                            service.transfer(
                              r.id,
                              recipient,
                              r.remaining,
                              account,
                              update,
                            ),
                          "Bond NFT transferred.",
                          "receipts",
                        );
                      }}
                    >
                      <label htmlFor={`recipient-${r.id}`}>
                        New owner address
                      </label>
                      <input
                        id={`recipient-${r.id}`}
                        value={recipient}
                        disabled={busy}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(e) => setRecipient(e.target.value.trim())}
                        placeholder="0x…"
                        required
                      />
                      {recipient &&
                        (!service.web3.utils.isAddress(recipient) ||
                          /^0x0{40}$/i.test(recipient) ||
                          recipient.toLowerCase() ===
                            account.toLowerCase()) && (
                          <p className="bond-inline-error">
                            Enter a valid address for a different owner.
                          </p>
                        )}
                      <p className="bond-caption">
                        This transfers all {number(human(r.remaining))}{" "}
                        remaining tokens’ claim rights, including unlocked
                        tokens. It cannot be undone by the project.
                      </p>
                      <button
                        className="bond-primary"
                        disabled={
                          busy ||
                          wrong ||
                          holdingsStale ||
                          !service.web3.utils.isAddress(recipient) ||
                          /^0x0{40}$/i.test(recipient) ||
                          recipient.toLowerCase() === account.toLowerCase()
                        }
                      >
                        Confirm transfer
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setTransfer(null)}
                      >
                        Cancel
                      </button>
                    </form>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </section>
      <section className="bond-faq">
        <h2>Before you buy</h2>
        <details>
          <summary>Do you accept private investments or OTC deals?</summary>
          <p>
            No. We do not accept private or OTC deals, negotiate insider
            allocations, or make under-the-table arrangements. We will not
            arrange investor meetings or pitch the platform to secure your
            purchase. The publicly available contracts and DEX are the routes to
            acquiring ADBYTES; there are no privately negotiated terms.
          </p>
          <p>
            Do your own research. If our public information, the platform and
            the smart contracts are not enough for you to make an independent
            decision, please keep your money. We do not want purchases that
            depend on private promises or persuasion.
          </p>
        </details>
        <details>
          <summary>Is there a fundraising target or deadline?</summary>
          <p>
            This is an open-ended, permissionless, on-chain program. There is no
            minimum fundraising target and no scheduled end date. You do not
            need an invitation, an investor meeting or our approval to use the
            contracts. Purchases depend on available inventory and contract
            conditions; the team can pause or close new sales.
          </p>
          <p>
            The contract normally requires a minimum purchase of 1 USDC, with an
            exception for buying the last remaining inventory. Each bonded
            purchase still has its own fixed release schedule.
          </p>
        </details>
        <details>
          <summary>How are the funds used?</summary>
          <p>
            70% goes to the project treasury to support platform operations,
            marketing and growth. The other 30% adds permanent full-range liquidity to our
            Uniswap v3 pool in the same transaction. The LP
            position NFT and its earned fees are locked and cannot be withdrawn.
          </p>
        </details>
        <details>
          <summary>Is participation optional, and what are the risks?</summary>
          <p>
            Participation is entirely optional and at your own risk. ADBYTES and
            Bond NFTs do not represent equity, and there is no guarantee of
            value, appreciation, profit or future liquidity. Tokens can lose
            their value, and smart contracts can have bugs. A vesting schedule
            guarantees neither a future price nor a buyer for your tokens or
            Bond NFT.
          </p>
        </details>
        <details>
          <summary>Can I verify the smart contracts myself?</summary>
          <p>
            Yes. The Smart contracts section below links to each contract’s
            source code and activity on BaseScan. Check the network and addresses
            yourself before buying; do not rely on addresses sent in private messages.
          </p>
        </details>
        <details>
          <summary>Why might my wallet show a security warning?</summary>
          <p>
            MetaMask and other wallets use security checks, including third-party
            providers. False positives can happen, and warnings may not explain
            the underlying evidence. ADBYTES is a small project with limited trading
            activity, but we cannot assume that explains any particular flag.
          </p>
          <p>
            Always use our official website,{" "}
            <a href="https://adbytes.media" target="_blank" rel="noreferrer">https://adbytes.media</a>,
            and confirm that the Base contract address in your wallet matches the
            BaseScan links below. These checks help confirm you are interacting
            with the intended ADBYTES contracts. Review the transaction details
            before deciding to proceed. A wallet warning alone does not prove
            malicious activity, but the correct address and verified source code
            are not a guarantee of safety. If you believe a warning is incorrect,
            request a review through your wallet’s support. MetaMask provides guidance on{" "}
            <a href="https://support.metamask.io/configure/wallet/security-alerts#how-to-report-a-false-classification" target="_blank" rel="noreferrer">reporting an incorrect classification</a>.
          </p>
        </details>
        <details>
          <summary>What is a Bond NFT?</summary>
          <p>
            Every purchase gives you a Bond NFT. With immediate delivery, your tokens arrive
            immediately and the NFT is marked fully claimed. Vested Bond NFTs hold a fixed
            allocation released linearly over the chosen schedule. This is not a loan, interest payment or equity.
            If 10% has been claimed and the NFT transfers, the new owner gets
            the remaining 90%, on the original schedule. Each Bond NFT can be
            claimed independently. Transferring it never resets previously claimed amounts.
          </p>
        </details>
        <details>
          <summary>Can the team change my allocation?</summary>
          <p>
            Purchased vested tokens are held in a separate escrow with no admin
            sweep. The team can pause new sales, change the treasury and recover
            unsold inventory, but those actions do not alter an existing bond’s
            schedule or backing.
          </p>
        </details>
        <details>
          <summary>What if the DEX is cheaper?</summary>
          <p>
            If you can get a better deal on the DEX or elsewhere, please take
            it. This program is entirely optional. The sale curve and DEX are
            separate markets; a discount here is relative to the immediate sale
            curve, not a promise of savings against another market.
          </p>
        </details>
        <details>
          <summary>Why can a purchase be temporarily unavailable?</summary>
          <p>
            Buying may be unavailable if inventory is empty, purchases are paused,
            or a current quote cannot be fetched. The page updates automatically.
            If a purchase fails, the payment and allocation both revert; gas can
            still be charged. Existing Bond NFTs remain claimable.
          </p>
        </details>
      </section>
      {service && (
        <footer className="bond-contracts">
          <h2>Smart contracts on {localBonds ? "Local Anvil" : "Base"}</h2>
          <p>Inspect the source code and activity using each explorer link.</p>
          {[
            ["Bond sale", service.config.sale],
            ["Bond NFT", service.config.receipt],
            ["Liquidity vault", service.config.vault],
            ["Permanent LP locker", service.config.locker],
            ["ETH payment adapter", service.config.nativeBuyer],
            ["ADBYTES token", service.config.token],
            ["USDC", service.config.usdc],
            ["Uniswap v3 pool", service.config.pool],
            ["Uniswap router", service.config.router],
            ["Uniswap position manager", service.config.positionManager],
            ["Uniswap quoter", service.config.quoter],
          ]
            .filter(([, a]) => a)
            .map(([label, address]) => (
              <span key={label}>
                <strong>{label}</strong>{" "}
                {explorer ? (
                  <a
                    href={`${explorer}/address/${address}#code`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${label}: ${address} — view on BaseScan`}
                  >
                    {address} <span aria-hidden="true">↗</span>
                  </a>
                ) : (
                  <code>{address}</code>
                )}
              </span>
            ))}
        </footer>
      )}
    </section>
  );
}
